import React, { useEffect, useRef } from 'react';

import { useWidgetServices } from '../services/services-context';
import { useWidgetStore } from '../state/store';

import { useDispositionCodes } from './hooks/use-disposition-codes';

const DRAFT_PREFIX = 'wrapup-draft-';
const DEBOUNCE_MS = 1000;

export function WrapUpPanel(): React.JSX.Element | null {
  const services = useWidgetServices();
  const wrapUp = useWidgetStore((s) => s.wrapUp);
  const setDisposition = useWidgetStore((s) => s.setDisposition);
  const setNotes = useWidgetStore((s) => s.setNotes);
  const saveStart = useWidgetStore((s) => s.saveWrapUpStart);
  const saveSuccess = useWidgetStore((s) => s.saveWrapUpSuccess);
  const saveError = useWidgetStore((s) => s.saveWrapUpError);
  const closeWrapUp = useWidgetStore((s) => s.closeWrapUp);
  const { codes, loading } = useDispositionCodes();
  const callId = wrapUp.callId;
  const rehydrated = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rehydrate draft on first mount for this callId — try API first, fall back to sessionStorage
  useEffect(() => {
    if (!callId || rehydrated.current) return;
    rehydrated.current = true;
    void (async () => {
      try {
        const draft = await services.calls.loadDraft(callId);
        if (draft) {
          if (typeof draft.notes === 'string') setNotes(draft.notes);
          if (typeof draft.disposition === 'string') setDisposition(draft.disposition);
          return;
        }
      } catch {
        // fall through to sessionStorage
      }
      try {
        const raw = sessionStorage.getItem(DRAFT_PREFIX + callId);
        if (!raw) return;
        const parsed = JSON.parse(raw) as { disposition?: string; notes?: string };
        if (typeof parsed.disposition === 'string') setDisposition(parsed.disposition);
        if (typeof parsed.notes === 'string') setNotes(parsed.notes);
      } catch {
        /* ignore corrupted draft */
      }
    })();
  }, [callId, setDisposition, setNotes, services.calls]);

  // Persist draft (debounced) — try API first, fall back to sessionStorage on failure
  useEffect(() => {
    if (!callId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void (async () => {
        const payload = {
          notes: wrapUp.notes,
          savedAt: new Date().toISOString(),
          ...(wrapUp.disposition != null ? { disposition: wrapUp.disposition } : {}),
        };
        try {
          await services.calls.saveDraft(callId, payload);
          try { sessionStorage.removeItem(DRAFT_PREFIX + callId); } catch { /* ignore */ }
        } catch {
          try {
            sessionStorage.setItem(DRAFT_PREFIX + callId, JSON.stringify(payload));
          } catch { /* ignore */ }
        }
      })();
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [callId, wrapUp.disposition, wrapUp.notes, services.calls]);

  if (!wrapUp.open || !callId) return null;

  const handleSave = async (): Promise<void> => {
    if (!wrapUp.disposition) return;
    saveStart();
    try {
      const r = await services.saveWrapUp(callId, { disposition: wrapUp.disposition, notes: wrapUp.notes });
      saveSuccess({ pending: r.pending });
      if (!r.pending) {
        sessionStorage.removeItem(DRAFT_PREFIX + callId);
      }
    } catch (err) {
      // Per spec §4.2, 409 zoho-call-id-missing with pending:true is a
      // valid pending-success state — the IS persisted local but couldn't
      // PATCH Zoho yet. Surface as pending instead of error so the widget
      // shows the retry-sync UI.
      const ae = err as { status?: number; body?: { error?: string; pending?: boolean } };
      if (ae?.status === 409 && ae.body?.error === 'zoho-call-id-missing' && ae.body?.pending) {
        saveSuccess({ pending: true });
        return;
      }
      saveError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleRetrySync = async (): Promise<void> => {
    saveStart();
    try {
      const r = await services.calls.retrySync(callId);
      saveSuccess({ pending: r.pending });
      if (!r.pending) {
        sessionStorage.removeItem(DRAFT_PREFIX + callId);
      }
    } catch (err) {
      saveError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDiscard = (): void => {
    sessionStorage.removeItem(DRAFT_PREFIX + callId);
    closeWrapUp();
  };

  return (
    <section style={{ marginTop: 16, padding: 12, border: '1px solid #ccc' }}>
      <h2>Wrap-up</h2>
      {loading && <p>Loading codes…</p>}
      {!loading && codes.length > 0 && (
        <label>
          Disposition
          <select
            aria-label="disposition"
            value={wrapUp.disposition ?? ''}
            onChange={(e) => setDisposition(e.target.value || null)}
            disabled={wrapUp.saving}
          >
            <option value="">— select —</option>
            {codes.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {!loading && codes.length === 0 && (
        <label>
          Disposition
          <input
            type="text"
            aria-label="disposition"
            value={wrapUp.disposition ?? ''}
            onChange={(e) => setDisposition(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
            placeholder="e.g. SOLD"
            disabled={wrapUp.saving}
          />
          <small> Tu administrador puede definir códigos predeterminados en Ajustes.</small>
        </label>
      )}
      <label>
        Notes
        <textarea
          aria-label="notes"
          value={wrapUp.notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          maxLength={10000}
          disabled={wrapUp.saving}
        />
      </label>
      {wrapUp.saveError && (
        <p style={{ color: 'red' }}>
          No se pudo guardar — {wrapUp.saveError}
        </p>
      )}
      {wrapUp.pending && (
        <p style={{ color: '#aa6600' }}>
          ⚠ Pendiente sincronizar con Zoho
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={() => void handleSave()} disabled={wrapUp.saving || !wrapUp.disposition}>
          {wrapUp.saving ? 'Saving…' : 'Save'}
        </button>
        {wrapUp.pending && (
          <button onClick={() => void handleRetrySync()} disabled={wrapUp.saving}>
            Reintentar sincronización
          </button>
        )}
        <button onClick={handleDiscard} disabled={wrapUp.saving}>
          Cerrar
        </button>
      </div>
    </section>
  );
}
